import React from 'react';
import { Link } from 'react-router-dom';
import Sparkline from './Sparkline';

/**
 * A headline number that goes somewhere.
 *
 * The old Overview was six numbers with no trend, no comparison and nothing
 * clickable — you landed on the panel and there was nowhere to go, which is
 * why it read as a dead end. A tile owes the reader three things:
 *
 *   the number       — what it is now
 *   the direction    — vs. the previous window of equal length, so "840" is
 *                      readable as good or bad without knowing the history
 *   a way in         — click through to the section that explains it, with
 *                      the current time range carried along
 *
 * `goodWhen` exists because direction is not universally good: signups rising
 * is good, try-on failure rate rising is not, and colouring both green
 * because the arrow points up is worse than not colouring them at all.
 */
function Delta({ delta, goodWhen }) {
  if (delta == null || Number.isNaN(delta)) return null;
  const rounded = Math.round(delta * 10) / 10;
  if (rounded === 0) {
    return <span className="text-xs font-medium text-ink-muted">no change</span>;
  }
  const up = rounded > 0;
  const isGood = goodWhen === 'flat' ? null : (up ? goodWhen === 'up' : goodWhen === 'down');
  const cls = isGood == null ? 'text-ink-secondary' : isGood ? 'text-status-good' : 'text-status-critical';
  return (
    <span className={`text-xs font-medium ${cls}`}>
      {up ? '▲' : '▼'} {Math.abs(rounded)}%
    </span>
  );
}

export default function KpiTile({
  label,
  value,
  caption,
  delta,
  goodWhen = 'up',
  trend,
  to,
  color = '#2a78d6',
  loading = false,
}) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-ink-secondary">{label}</span>
        {to && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="mt-1 flex-shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5" aria-hidden="true">
            <path d="M4 2.5L7.5 6L4 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tracking-tight text-ink">
          {loading ? <span className="inline-block h-7 w-16 animate-pulse rounded bg-hairline" /> : value}
        </span>
        {!loading && <Delta delta={delta} goodWhen={goodWhen} />}
      </div>
      {trend && trend.length > 1 && (
        <div className="mt-2">
          <Sparkline values={trend} color={color} height={26} />
        </div>
      )}
      {caption && <div className="mt-1.5 text-xs leading-snug text-ink-muted">{caption}</div>}
    </>
  );

  const shell = 'group block rounded-xl border border-hairline bg-surface px-4 py-3.5 shadow-sm';

  if (!to) return <div className={shell}>{body}</div>;
  return (
    <Link to={to} className={`${shell} transition-colors hover:border-baseline hover:bg-surface-page`}>
      {body}
    </Link>
  );
}
